import React, { useState, useEffect } from "react"
import {
    ProductFields,
    TreeNodeData,
    TreeSelector,
} from "@tenforce/product-fields"
import {
    IOption,
    TreeSelectorLabels,
} from "@tenforce/product-fields/treeSelector"
import { Symbols } from "@tenforce/toolbox-fontmap"

import { ITreeSelectorField } from "../.."
import { GlobalState } from "../../../../store"
import { useAppDispatch, useAppSelector } from "../../../../hooks"
import {
    initialTreeSelectorData,
    isFieldItemSelect,
    transformToTreeNodeData,
} from "./utils"
import { typeHasGroups } from "../../../../apis/treeSelector/utils"
import {
    getValues,
    getChildren,
    getSelectedChildren,
    getSelectedValues,
    getChildrenForGroup,
    mapToGroupedTreeStructure,
} from "../../../../apis/treeSelector/treeSelector"
import { SelectableValue } from "../../../../apis/treeSelector/type"
import { updateField } from "../../slice"
import { Labels } from "../../../../util/Translations"
import { formatString } from "../../../../util/Strings"
import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"

const {
    ClickHereToEdit,
    LoadNMore,
    LoadNMoreOf,
    NothingFound,
    FilterAllNValues,
    FilterNSelectedValues,
    ShowSelectedValues,
    ShowAllValues,
} = Labels

const PAGE_SIZE = 20

type Props = ITreeSelectorField

export default function TreeSelectorComponent(props: Props): JSX.Element {
    const dispatch = useAppDispatch()
    const { itemId, fieldId, odataType: type, value, isSingleSelect } = props
    const { isLoading: globalIsLoading } = useAppSelector(
        (state: GlobalState) => state.fields,
    )

    const [filter, setFilter] = useState<string>("")
    const [skip, setSkip] = useState(0)
    const [isSaving, setIsSaving] = useState(false)
    const [selectableList, setSelectableList] = useState<
        IOption<SelectableValue>
    >(initialTreeSelectorData)
    const [isSelectableLoading, setIsSelectableLoading] = useState(false)

    const [selectedList, setSelectedList] = useState<IOption<SelectableValue>>(
        initialTreeSelectorData,
    )
    const [selectedValues, setSelectedValues] = useState<
        TreeNodeData<SelectableValue>[]
    >([])
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [isSelectedLoading, setIsSelectedLoading] = useState(false)
    const isGrouped = typeHasGroups(type)
    const fieldOptions = {
        ...props,
        isLoading: isSaving || globalIsLoading,
        description: (
            <HTMLAndInternalLinkParser
                content={(props?.description as string) ?? ""}
            />
        ),
    } as ProductFields.FieldProps

    const isItemSelect = isFieldItemSelect(type)

    const parseSelectedTree = (): void => {
        const tempSelectedValues = [...value]
        const transformedData = transformToTreeNodeData(
            tempSelectedValues,
            isGrouped,
        )
        setSelectedValues(transformedData)
        setSelectedList({
            ...selectedList,
            data: transformedData,
            totalCount: value.length,
            hasMore: false,
            currentSkip: 0,
        })
    }

    useEffect(() => {
        if (value && value.length) {
            setSelectedIds(value.map((item) => `${item?.id}`))
            parseSelectedTree()
        } else {
            setSelectedIds([])
            setSelectedValues([])
            setSelectedList({
                ...selectedList,
                data: [],
                totalCount: 0,
                hasMore: false,
                currentSkip: 0,
            })
        }
    }, [value])

    const onModalOpen = (): void => {
        setSkip(0)
        setFilter("")
    }

    const fetchSelectableOptions = async (
        filterValue: string,
    ): Promise<void> => {
        const isFilterChanged = filterValue !== filter
        const effectiveSkip = isFilterChanged ? 0 : skip

        try {
            setIsSelectableLoading(true)
            if (isGrouped) {
                const valueResult = await getChildren(
                    itemId,
                    fieldId,
                    type,
                    filterValue,
                    effectiveSkip,
                    PAGE_SIZE,
                    [],
                )

                const decoratedData = mapToGroupedTreeStructure(
                    valueResult.data || [],
                    type,
                    selectedIds,
                )

                let newData = decoratedData
                let nextSkip = 0
                let hasMore = false

                if (isFilterChanged) {
                    // Filter changed - reset skip and start fresh
                    setFilter(filterValue)
                    newData = decoratedData
                    nextSkip = valueResult.data.length
                    hasMore = nextSkip < valueResult.totalChildrenCount
                    setSkip(nextSkip)
                } else if (skip === 0) {
                    // Initial load with same/empty filter
                    newData = decoratedData
                    nextSkip = valueResult.data.length
                    hasMore = nextSkip < valueResult.totalChildrenCount
                    setSkip(nextSkip)
                } else {
                    // Load more - merge data into existing groups
                    const existingGroupMap = new Map<
                        string,
                        TreeNodeData<SelectableValue>
                    >()
                    selectableList.data.forEach((item) => {
                        existingGroupMap.set(item.id, { ...item })
                    })

                    decoratedData.forEach((newItem) => {
                        if (existingGroupMap.has(newItem.id)) {
                            const existingGroup = existingGroupMap?.get(
                                newItem.id,
                            )
                            if (existingGroup?.children && newItem.children) {
                                existingGroup.children = [
                                    ...existingGroup.children,
                                    ...newItem.children,
                                ]
                            }
                        } else {
                            existingGroupMap.set(newItem.id, newItem)
                        }
                    })

                    newData = Array.from(existingGroupMap.values())
                    nextSkip = skip + (valueResult.data?.length || 0)
                    hasMore = nextSkip < valueResult.totalChildrenCount
                    setSkip(nextSkip)
                }

                if (!isItemSelect) {
                    newData = newData.map((item, index) => {
                        const isGroupedAndLast =
                            index === newData.length - 1 &&
                            item.children &&
                            item.hasChildren
                        return {
                            ...item,
                            sohuldShowGroupCheckBox: !isGroupedAndLast,
                        }
                    })
                }

                setSelectableList({
                    data: newData,
                    totalCount: valueResult.totalChildrenCount,
                    hasMore,
                    currentSkip: nextSkip,
                })
            } else {
                const result = await getValues(
                    itemId,
                    fieldId,
                    type,
                    filterValue,
                    effectiveSkip,
                    PAGE_SIZE,
                    [],
                )

                let newData = result.data as TreeNodeData<SelectableValue>[]
                newData = newData.map((item) => ({
                    ...item,
                    isSelected: selectedIds.includes(`${item.id}`),
                }))
                let nextSkip = 0
                let hasMore = false

                if (isFilterChanged) {
                    setFilter(filterValue)
                    nextSkip = newData.length
                    hasMore = nextSkip < result.totalChildrenCount
                    setSkip(nextSkip)
                } else if (effectiveSkip === 0) {
                    nextSkip = newData.length
                    hasMore = nextSkip < result.totalChildrenCount
                    setSkip(nextSkip)
                } else {
                    newData = selectableList.data.concat(newData || [])
                    nextSkip = effectiveSkip + (result.data?.length || 0)
                    hasMore = nextSkip < result.totalChildrenCount
                    setSkip(nextSkip)
                }

                setSelectableList({
                    data: newData,
                    totalCount: result.totalChildrenCount,
                    hasMore,
                    currentSkip: nextSkip,
                })
            }
        } catch (error) {
            console.error(error)
            throw error
        } finally {
            setIsSelectableLoading(false)
        }
    }

    const fetchSelectedOptions = async (
        filterValue: string,
        selectedIdsParam: string[],
    ): Promise<void> => {
        if (filter !== filterValue) {
            setFilter(filterValue)
        }

        if (selectedIdsParam.length === 0) {
            setSelectedList({
                data: [],
                totalCount: 0,
                hasMore: false,
                currentSkip: 0,
            })
        } else {
            try {
                setIsSelectedLoading(true)
                if (isSingleSelect) {
                    if (selectedIds.length > 0) {
                        let passedFilter = false
                        passedFilter = value[0].label
                            .toLowerCase()
                            .includes(filterValue.trim().toLowerCase())
                        if (!passedFilter) {
                            setSelectedList({
                                data: [],
                                totalCount: 0,
                                hasMore: false,
                                currentSkip: 0,
                            })
                        } else {
                            //if selectedList already have data then no need to set treeData
                            selectedList.data.length === 0
                                ? parseSelectedTree()
                                : ""
                        }
                    }
                } else if (isGrouped) {
                    const valueResult = await getSelectedChildren(
                        type,
                        filterValue,
                        selectedIdsParam,
                    )

                    const decoratedData = mapToGroupedTreeStructure(
                        valueResult.data || [],
                        type,
                        selectedIds,
                    )

                    setSelectedList({
                        data: decoratedData,
                        totalCount: valueResult.totalChildrenCount,
                        hasMore: false,
                        currentSkip: 0,
                    })
                } else {
                    const result = await getSelectedValues(
                        type,
                        filterValue,
                        selectedIdsParam,
                    )

                    setSelectedList({
                        data: result.data as TreeNodeData<SelectableValue>[],
                        totalCount: result.totalChildrenCount,
                        hasMore: false,
                        currentSkip: 0,
                    })
                }
            } catch (error) {
                console.error(error)
                throw error
            } finally {
                setIsSelectedLoading(false)
            }
        }
    }

    const fetchAllNodeChildrenIds = async (
        nodeId: string,
    ): Promise<string[]> => {
        try {
            const valueResult = await getChildrenForGroup(
                itemId,
                fieldId,
                type,
                nodeId,
            )

            const childIds = valueResult.data.map((item) => `${item.id}`)

            return childIds || []
        } catch (error) {
            console.error(error)
            throw error
        }
    }

    const onLoadMore = (filterValue: string): void => {
        fetchSelectableOptions(filterValue)
    }

    const onSave = async (value: string[]): Promise<void> => {
        try {
            setIsSaving(true)
            await dispatch(
                updateField({
                    field: props,
                    newValue: value,
                }),
            ).unwrap()
        } catch (error) {
            console.error(error)
            throw error
        } finally {
            setIsSaving(false)
            setFilter("")
        }
    }

    const treeSelectorLabels = {
        EmptyPlaceholder: ClickHereToEdit,
        FilterAllNValues: (count: number) =>
            Symbols.Search + formatString(FilterAllNValues, count),
        FilterNSelectedValues: (count: number) =>
            Symbols.Search + formatString(FilterNSelectedValues, count),
        FilterNoResults: NothingFound,
        LoadNMore: (remaining: number) => formatString(LoadNMore, remaining),
        LoadNMoreOf: (pageSize: number, remaining: number) =>
            formatString(LoadNMoreOf, pageSize, remaining),
        ShowAllValuesTooltip: ShowAllValues,
        ShowSelectedValuesTooltip: ShowSelectedValues,
    } as TreeSelectorLabels

    return (
        <div>
            <TreeSelector
                {...fieldOptions}
                shouldHaveOverlay={true}
                isSelectedViewEnabled={true}
                isLoading={fieldOptions.isLoading || isSaving}
                selectedOptions={{
                    fetchOptions: fetchSelectedOptions,
                    options: selectedList,
                    isLoading: isSelectedLoading,
                    selectedIds: selectedIds,
                    selectedValues: selectedValues,
                }}
                selectableOptions={{
                    options: selectableList,
                    fetchOptions: fetchSelectableOptions,
                    isLoading: isSelectableLoading,
                    onLoadMore: onLoadMore,
                }}
                shouldTreatGroupAsValue={isItemSelect}
                searchBar={{
                    isEnabled: true,
                    value: filter,
                }}
                isSingleSelect={isSingleSelect}
                onSave={onSave}
                isGrouped={isGrouped}
                onModalOpen={onModalOpen}
                fetchAllNodeChildren={fetchAllNodeChildrenIds}
                labels={treeSelectorLabels}
            />
        </div>
    )
}
